/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
/** @jsxImportSource solid-js */

import { Component, JSXElement, Show } from 'solid-js';
import Spinner from '../Spinner/Spinner';

export type ButtonType = 'primary' | 'upgrade';

export interface RestrictedAccessSplashScreenProps {
  title?: string;
  subHeading?: string;
  description?: string;
  showIcon?: boolean;
  linkText?: string;
  linkUrl?: string;
  buttonLabel?: string;
  showBackgroundImage?: boolean;
  buttonType?: ButtonType;
  onButtonClick?(): void;
  onLinkClick?(): void;
  isLoading?: boolean;
  content?: JSXElement; // For additional content if needed
}

const RestrictedAccessSplashScreen: Component<
  RestrictedAccessSplashScreenProps
> = (props): JSXElement => {
  // Reactive getters for props
  const getTitle = () => props.title || '';
  const getSubHeading = () => props.subHeading || '';
  const getDescription = () => props.description || '';
  const getShowIcon = () => props.showIcon || false;
  const getLinkText = () => props.linkText || '';
  const getLinkUrl = () => props.linkUrl || '';
  const getButtonLabel = () => props.buttonLabel || '';
  const getShowBackgroundImage = () => props.showBackgroundImage || false;
  const getButtonType = () => props.buttonType || 'primary';
  const getIsLoading = () => props.isLoading || false;

  // Asset paths - using relative paths since assets are in public folder
  const getImgSrc = () => '/assets/img/free-version.png';
  const getApiFailureImage = () => '/assets/img/bg_landscape.png';

  // Background style computation
  const getBackgroundStyle = () => {
    if (getShowBackgroundImage()) {
      return {
        'background-image': `url(${getApiFailureImage()})`,
      };
    }
    return {};
  };

  // Content transform style computation
  const getContentStyle = () => {
    if (getShowBackgroundImage()) {
      return {
        transform: 'translateY(-50%)',
      };
    }
    return {};
  };

  // Button class computation
  const getButtonClasses = () => {
    const classes = ['button', 'mt-1'];
    classes.push(getButtonType());
    return classes.join(' ');
  };

  // Event handlers
  const handleButtonClick = () => {
    props.onButtonClick?.();
  };

  const handleLinkClick = () => {
    props.onLinkClick?.();
  };

  return (
    <Show when={!getIsLoading()} fallback={<Spinner show={true} />}>
      <div class="splash-screen-container" style={getBackgroundStyle()}>
        <Show when={getShowIcon()}>
          <img src={getImgSrc()} alt="Lock Icon" class="popup-icon" />
        </Show>

        <div style={getContentStyle()}>
          <div class="splash-screen-header">
            <Show when={getTitle()}>
              <h1 class="heading">{getTitle()}</h1>
            </Show>
            <Show when={getSubHeading()}>
              <h2 class="sub-heading">{getSubHeading()}</h2>
            </Show>
          </div>

          <Show when={getDescription()}>
            <div class="splash-screen-body">
              <p>{getDescription()}</p>
            </div>
          </Show>

          <div class="splash-screen-footer mt-2">
            <Show when={getButtonLabel()}>
              <button class={getButtonClasses()} onClick={handleButtonClick}>
                {getButtonLabel()}
              </button>
            </Show>

            <Show when={getLinkText()}>
              <a
                href={getLinkUrl()}
                class="popup-link"
                rel="noopener noreferrer"
                onClick={handleLinkClick}
              >
                {getLinkText()}
              </a>
            </Show>
          </div>
        </div>

        {/* Render additional content if provided */}
        <Show when={props.content}>{props.content}</Show>
      </div>
    </Show>
  );
};

export default RestrictedAccessSplashScreen;
